import { Download, Share, X } from 'lucide-react'
import { useInstallPrompt } from '../../lib/pwa/useInstallPrompt'
import styles from './InstallBanner.module.css'

interface InstallBannerProps {
  /**
   * Cómo se nombra la app en el mensaje (ej. "esta invitación", "la galería",
   * "la consola"). Default genérico.
   */
  label?: string
}

/**
 * Banner in-app para instalar la PWA. Fijo abajo, no bloquea la UI, descartable.
 * Se muestra solo si la app es instalable y no fue descartada/ya instalada (la
 * decisión vive en `useInstallPrompt`). En iOS Safari muestra las instrucciones
 * manuales ("Compartir → Agregar a inicio") porque iOS no soporta el prompt.
 *
 * Hereda el tema del subtree donde se monte (`var(--color-*)`), así en una
 * invitación toma los colores del evento y en la consola los de Tivy.
 */
export function InstallBanner({ label = 'la app' }: InstallBannerProps) {
  const { canShow, isIOS, hasPrompt, promptInstall, dismiss } = useInstallPrompt()

  if (!canShow) return null

  return (
    <div className={styles.banner} role="dialog" aria-label="Instalar aplicación">
      <div className={styles.body}>
        <Download size={20} aria-hidden className={styles.icon} />
        <p className={styles.text}>
          {hasPrompt ? (
            <>Instalá {label} para tenerla a mano, sin abrir el navegador.</>
          ) : isIOS ? (
            <>
              Agregá {label} a tu inicio: tocá{' '}
              <Share size={14} aria-hidden className={styles.shareIcon} /> Compartir
              {' → '}
              <strong>Agregar a pantalla de inicio</strong>.
            </>
          ) : null}
        </p>
      </div>

      <div className={styles.actions}>
        {hasPrompt && (
          <button
            type="button"
            className={styles.installBtn}
            onClick={() => void promptInstall()}
          >
            Instalar
          </button>
        )}
        <button
          type="button"
          className={styles.dismissBtn}
          onClick={dismiss}
          aria-label="No instalar ahora"
        >
          <X size={18} aria-hidden />
        </button>
      </div>
    </div>
  )
}
